import { noop } from "lodash";
import * as React from "react";

import { Error, Info } from "@livechat/design-system-icons";
import {
  Button,
  ButtonProps,
  DesignToken,
  Heading,
  Icon,
  IconSource,
  Modal,
  ModalHeader,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { isInIframe } from "helpers/app";
import { withVar } from "themes/helpers";

type IModalButtonProps = {
  label: string;
  dataTestId?: string;
  actionType: "primary" | "destructive";
} & Omit<ButtonProps, "children" | "size" | "kind">;

interface IProps {
  icon?: IconSource;
  iconColor?: string;
  heading?: string;
  primaryAction: IModalButtonProps;
  secondaryAction?: Omit<IModalButtonProps, "actionType">;
  children: React.ReactNode;
  onClose?: () => void;
}

/**
 * ConfirmActionModal
 *
 * @example
 *   <ConfirmActionModal
 *     heading="Are you sure?"
 *     icon={Check} // by default it's Info or Error depending on actionType
 *     primaryAction={{
 *       label: 'Yes',
 *       actionType: 'destructive',
 *       onClick: () => console.log('Yes clicked'),
 *     }}
 *     secondaryAction={{
 *       label: 'No',
 *       onClick: () => console.log('No clicked'),
 *     }}
 *   >
 *     Are you sure?
 *   </ConfirmActionModal>
 */

const ConfirmActionModal: React.FC<IProps> = ({
  heading,
  onClose,
  icon,
  iconColor = withVar(DesignToken.ContentBasicDisabled),
  primaryAction,
  secondaryAction,
  children,
}) => {
  const isEmbeddedInIframe = isInIframe();

  const { label: primaryLabel, dataTestId: primaryDataTestId, actionType, ...primaryActionProps } = primaryAction || {};

  const { label: secondaryLabel, dataTestId: secondaryDataTestId, ...secondaryActionProps } = secondaryAction || {};

  return (
    <Modal onClose={onClose ? onClose : noop} heading={<ModalHeader />}>
      <Flex
        column
        alignItems="center"
        paddingH={withVar(SpacingToken.Spacing9)}
        paddingBottom={withVar(SpacingToken.Spacing10)}
        gap={withVar(SpacingToken.Spacing3)}
        maxWidth="470px"
      >
        <Icon
          size="xxlarge"
          source={icon || (actionType === "destructive" ? Error : Info)}
          customColor={iconColor || withVar(DesignToken.ContentBasicDisabled)}
        />
        <Flex column alignItems="center" gap={withVar(SpacingToken.Spacing2)} css={{ textAlign: "center" }}>
          <Heading data-testid="modal-heading">{heading}</Heading>
          <Block>{typeof children === "string" ? <Text>{children}</Text> : children}</Block>
        </Flex>
      </Flex>
      <Flex
        gap={withVar(SpacingToken.Spacing2)}
        width="100%"
        justifyContent="center"
        marginBottom={withVar(SpacingToken.Spacing9)}
      >
        {secondaryAction && (
          <Button kind="secondary" size="large" data-testid={secondaryDataTestId} {...secondaryActionProps}>
            {secondaryLabel || "Cancel"}
          </Button>
        )}
        <Button
          size="large"
          kind={actionType === "primary" && isEmbeddedInIframe ? "high-contrast" : actionType}
          data-testid={primaryDataTestId}
          {...primaryActionProps}
        >
          {primaryLabel || "Confirm"}
        </Button>
      </Flex>
    </Modal>
  );
};

export default ConfirmActionModal;
